// ================================================================================================
// MultiEmailInput Component
// ================================================================================================
// Advanced email/group selector with Azure Graph API integration
// Supports dual search modes (Users/Groups), DL member viewing, and intelligent pattern detection
// ================================================================================================

// React & Core Dependencies
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

// UI Components - Organized by category
import {
  // Form Components
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,

  // Layout Components
  Box,
  Flex,
  VStack,
  HStack,
  SimpleGrid,
  Divider,

  // Interactive Components
  Button,
  IconButton,
  Switch,
  Tag,
  TagLabel,
  Badge,
  Avatar,
  Tooltip,

  // Feedback Components
  Alert,
  AlertIcon,
  Spinner,
  Text,

  // Modal Components
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';

// External Libraries
import { AsyncSelect } from 'chakra-react-select';
import { ViewIcon } from '@chakra-ui/icons';
import axios from 'axios';
import debounce from 'lodash.debounce';

// ================================================================================================
// CONSTANTS & CONFIGURATION
// ================================================================================================

/** Search type enumeration */
const SEARCH_TYPES = {
  USERS: 'users',
  GROUPS: 'groups',
};

/** Distribution List pattern matching for Trend Micro */
const DL_PATTERNS = {
  ALLOF_PREFIX: 'allof',
  DL_DOMAIN: '@dl.trendmicro.com',
};

/** Azure Graph API endpoints */
const API_ENDPOINTS = {
  USERS: '/graph/users',
  GROUPS: '/graph/groups',
};

/** UI Configuration */
const UI_CONFIG = {
  DEBOUNCE_DELAY: 1000,
  TEAL_THEME: 'teal',
  MODAL_SIZE: 'xl',
  TAG_SIZE: 'md',
  SPINNER_SIZE: 'xl',
  MAX_MODAL_HEIGHT: '500px',
};

/** Styling constants for consistent theming */
const THEME_COLORS = {
  primary: '#319795', // teal.500
  primaryHover: '#2c7a7b', // teal.600
  background: '#f7fafc', // gray.50
  border: 'gray.200',
  text: {
    primary: '#4a5568', // gray.600
    secondary: 'gray.500',
    muted: 'gray.400',
  },
};

// ================================================================================================
// UTILITY FUNCTIONS
// ================================================================================================

/**
 * Creates a formatted user option for the dropdown
 * @param {Object} user - User data from API
 * @returns {Object} Formatted option object
 */
const createUserOption = (user) => ({
  label: `${user.name} (${user.email})`,
  value: user.email,
  type: SEARCH_TYPES.USERS,
  data: user,
});

/**
 * Creates a formatted group option for the dropdown
 * @param {Object} group - Group data from API
 * @returns {Object} Formatted option object
 */
const createGroupOption = (group) => ({
  label: group.name,
  value: group.email,
  type: SEARCH_TYPES.GROUPS,
  data: group,
});

/**
 * Sanitizes display name by removing parenthetical content
 * @param {string} name - Raw display name
 * @returns {string} Cleaned display name
 */
const sanitizeDisplayName = (name) => {
  if (!name) return '';
  return name.replace(/\s*\([^)]*\)/g, '').trim();
};

// ================================================================================================
// MAIN COMPONENT
// ================================================================================================

/**
 * MultiEmailInput - Advanced email/group selector component
 *
 * Features:
 * - Dual search modes: Users and Distribution Lists
 * - Visual differentiation with consistent teal theming
 * - Group member viewing for distribution lists
 * - Intelligent DL detection based on email patterns
 * - Responsive design with tooltips and animations
 * - Comprehensive error handling and validation
 *
 * @component
 * @param {Object} props - Component properties
 * @param {string} props.label - Form control label
 * @param {string} props.helperText - Descriptive helper text
 * @param {Array<Object>} props.selectedItems - Currently selected users/groups
 * @param {boolean} props.isError - Error state flag
 * @param {string} props.errorMessage - Error message to display
 * @param {Function} props.onItemAdd - Callback for adding new selection
 * @param {Function} props.onItemRemove - Callback for removing selection
 * @param {('users'|'groups')} props.defaultSearchType - Initial search mode
 */
function MultiEmailInput({
  label,
  helperText,
  selectedItems,
  isError,
  errorMessage,
  onItemAdd,
  onItemRemove,
  defaultSearchType = SEARCH_TYPES.USERS,
}) {
  // ==============================================================================================
  // STATE MANAGEMENT
  // ==============================================================================================

  /** Current search type (users or groups) */
  const [searchType, setSearchType] = useState(defaultSearchType);

  /** Modal state for group member viewing */
  const { isOpen, onOpen, onClose } = useDisclosure();

  /** Group members data for modal display */
  const [groupMembers, setGroupMembers] = useState([]);

  /** Currently selected group for member viewing */
  const [selectedGroup, setSelectedGroup] = useState(null);

  /** Loading state for group member fetching */
  const [loadingMembers, setLoadingMembers] = useState(false);

  // ==============================================================================================
  // DISTRIBUTION LIST DETECTION
  // ==============================================================================================

  /**
   * Determines if an email belongs to a distribution list
   * Uses Trend Micro specific patterns for accurate DL detection
   *
   * @param {Object} item - Selected item object
   * @param {string} item.value - Email address
   * @param {string} item.type - Item type ('user' or 'group')
   * @returns {boolean} True if item is a distribution list
   */
  const isDistributionList = useCallback((item) => {
    if (!item?.value) return false;

    const email = item.value.toLowerCase();

    // Trend Micro DL detection patterns:
    // 1. Emails starting with "allof" (e.g., allofmarketing@trendmicro.com)
    // 2. Emails using DL subdomain (e.g., team@dl.trendmicro.com)
    return (
      email.startsWith(DL_PATTERNS.ALLOF_PREFIX) ||
      email.includes(DL_PATTERNS.DL_DOMAIN)
    );
  }, []);

  // ==============================================================================================
  // API INTEGRATION - SEARCH FUNCTIONS
  // ==============================================================================================

  /** Ref to store the current debounced function for cleanup */
  const debouncedSearchRef = useRef(null);

  /**
   * Creates a stable debounced search function using useMemo
   * This ensures the debounced function is not recreated on every render
   */
  const debouncedSearch = useMemo(() => {
    const searchFunction = debounce(
      (inputValue, callback, searchEndpoint, mapFunction) => {
        // Validate input
        if (!inputValue?.trim()) {
          callback([]);
          return;
        }

        const query = inputValue.trim();

        axios
          .get(`${searchEndpoint}?query=${encodeURIComponent(query)}`)
          .then((response) => {
            const results = response.data?.map(mapFunction) || [];
            callback(results);
          })
          .catch(() => {
            callback([]);
          });
      },
      UI_CONFIG.DEBOUNCE_DELAY
    );

    // Store reference for cleanup
    debouncedSearchRef.current = searchFunction;
    return searchFunction;
  }, []); // Empty dependency array ensures this is created only once

  /**
   * Cleanup debounced function on unmount
   */
  useEffect(() => {
    return () => {
      if (debouncedSearchRef.current) {
        debouncedSearchRef.current.cancel();
      }
    };
  }, []);

  /**
   * Debounced function to search users via Azure Graph API
   * Searches by name or email and returns formatted options
   *
   * @param {string} inputValue - Search query
   * @param {Function} callback - Callback function for results
   */
  const loadUsers = useCallback(
    (inputValue, callback) => {
      debouncedSearch(
        inputValue,
        callback,
        API_ENDPOINTS.USERS,
        createUserOption
      );
    },
    [debouncedSearch]
  );

  /**
   * Debounced function to search groups via Azure Graph API
   * Searches by name or email and returns formatted options
   *
   * @param {string} inputValue - Search query
   * @param {Function} callback - Callback function for results
   */
  const loadGroups = useCallback(
    (inputValue, callback) => {
      debouncedSearch(
        inputValue,
        callback,
        API_ENDPOINTS.GROUPS,
        createGroupOption
      );
    },
    [debouncedSearch]
  );

  // ==============================================================================================
  // EVENT HANDLERS
  // ==============================================================================================

  /**
   * Handles item selection from dropdown
   * Prevents duplicate selections and validates input
   *
   * @param {Object|null} selectedOption - Selected item from dropdown
   */
  const handleItemSelect = useCallback(
    (selectedOption) => {
      if (!selectedOption?.value) return;

      // Check for duplicate selections (both value and type must match)
      const isDuplicate = selectedItems.some(
        (item) =>
          item.value === selectedOption.value &&
          item.type === selectedOption.type
      );

      if (!isDuplicate) {
        onItemAdd(selectedOption);
      }
    },
    [selectedItems, onItemAdd]
  );

  /**
   * Toggles between user and group search modes
   * Updates search type state and preserves current selections
   */
  const handleSearchTypeToggle = useCallback(() => {
    setSearchType((prevType) =>
      prevType === SEARCH_TYPES.USERS ? SEARCH_TYPES.GROUPS : SEARCH_TYPES.USERS
    );
  }, []);

  /**
   * Fetches and displays members of a selected distribution list
   * Handles both ID-based and email-based group identification
   *
   * @param {Object} group - Group object containing id/email and metadata
   */
  const handleViewGroupMembers = useCallback(
    async (group) => {
      // Initialize modal state
      setSelectedGroup(group);
      setLoadingMembers(true);
      setGroupMembers([]);
      onOpen();

      try {
        let groupId = group.data?.id;

        // Fallback: Search for group by email if no ID available
        if (!groupId && group.value) {
          const searchResponse = await axios.get(
            `${API_ENDPOINTS.GROUPS}?query=${encodeURIComponent(group.value)}`
          );

          if (searchResponse.data?.length > 0) {
            groupId = searchResponse.data[0].id;
          } else {
            throw new Error('No group found with the provided email address');
          }
        }

        // Validate group ID
        if (!groupId) {
          throw new Error('Unable to determine group ID for fetching members');
        }

        // Fetch group members
        const response = await axios.get(
          `${API_ENDPOINTS.GROUPS}/${groupId}/members`
        );
        setGroupMembers(response.data || []);
      } catch (error) {
        setGroupMembers([]);
      } finally {
        setLoadingMembers(false);
      }
    },
    [onOpen]
  );

  // ==============================================================================================
  // RENDER HELPERS
  // ==============================================================================================

  /**
   * Renders the search type toggle component
   */
  const renderSearchTypeToggle = () => (
    <Flex justify="space-between" align="center" mb={4}>
      <Text fontSize="sm" fontWeight="medium" color={THEME_COLORS.text.primary}>
        Search Type:
      </Text>
      <HStack
        spacing={3}
        bg={THEME_COLORS.background}
        p={2}
        borderRadius="md"
        border="1px"
        borderColor={THEME_COLORS.border}
      >
        <Text
          fontSize="sm"
          fontWeight={searchType === SEARCH_TYPES.USERS ? 'semibold' : 'normal'}
          color={
            searchType === SEARCH_TYPES.USERS
              ? 'blue.600'
              : THEME_COLORS.text.secondary
          }
          transition="all 0.2s"
        >
          Users
        </Text>
        <Switch
          colorScheme={UI_CONFIG.TEAL_THEME}
          isChecked={searchType === SEARCH_TYPES.GROUPS}
          onChange={handleSearchTypeToggle}
          size="sm"
        />
        <Text
          fontSize="sm"
          fontWeight={
            searchType === SEARCH_TYPES.GROUPS ? 'semibold' : 'normal'
          }
          color={
            searchType === SEARCH_TYPES.GROUPS
              ? 'green.600'
              : THEME_COLORS.text.secondary
          }
          transition="all 0.2s"
        >
          Groups
        </Text>
      </HStack>
    </Flex>
  );

  /**
   * Renders the informational alert for group limitations
   */
  const renderGroupAlert = () =>
    searchType === SEARCH_TYPES.GROUPS && (
      <Alert status="info" size="sm" mb={3} borderRadius="md">
        <AlertIcon />
        <Text fontSize="xs">
          Note: Nested distribution lists are not supported. Only direct group
          members will be included.
        </Text>
      </Alert>
    );

  /**
   * Renders the search input with enhanced styling
   */
  const renderSearchInput = () => (
    <VStack align="stretch" spacing={3} mb={4}>
      <AsyncSelect
        placeholder={
          searchType === SEARCH_TYPES.USERS
            ? '🔍 Search user by name or email...'
            : "🔍 Search by distribution list's name or email..."
        }
        loadOptions={searchType === SEARCH_TYPES.USERS ? loadUsers : loadGroups}
        onChange={handleItemSelect}
        isClearable
        value={null} // Always reset after selection
        styles={{
          control: (provided) => ({
            ...provided,
            borderColor: THEME_COLORS.primary,
            borderWidth: '2px',
            boxShadow: 'none',
            backgroundColor: THEME_COLORS.background,
            '&:hover': {
              borderColor: THEME_COLORS.primaryHover,
            },
          }),
          placeholder: (provided) => ({
            ...provided,
            color: THEME_COLORS.text.primary,
            fontWeight: '500',
          }),
        }}
      />

      <Flex
        justify="space-between"
        align="center"
        fontSize="sm"
        color={THEME_COLORS.text.primary}
      >
        <HStack spacing={2}>
          <Text>💡</Text>
          <Text>
            {searchType === SEARCH_TYPES.USERS
              ? 'Tip: Search by name or email to find users'
              : 'Tip: Click the eye icon on selected groups to view members'}
          </Text>
        </HStack>

        {selectedItems.length > 0 && (
          <Badge
            colorScheme={UI_CONFIG.TEAL_THEME}
            variant="subtle"
            fontSize="xs"
          >
            {selectedItems.length} selected
          </Badge>
        )}
      </Flex>
    </VStack>
  );

  /**
   * Renders individual selected item tag with actions
   */
  const renderSelectedItem = (item) => (
    <Tag
      key={`${item.value}-${item.type}`}
      size={UI_CONFIG.TAG_SIZE}
      mr={2}
      mb={1}
      borderRadius="full"
      variant="solid"
      colorScheme={UI_CONFIG.TEAL_THEME}
      display="inline-flex"
      alignItems="center"
    >
      <TagLabel>
        {item.type === SEARCH_TYPES.USERS
          ? item.data?.name || item.label
          : item.label}
        {item.type === SEARCH_TYPES.USERS && ` (${item.value})`}
      </TagLabel>

      {/* View Members Button - Only for Distribution Lists */}
      {isDistributionList(item) && (
        <Tooltip
          label="View group members"
          fontSize="xs"
          placement="top"
          hasArrow
        >
          <IconButton
            aria-label="View group members"
            icon={<ViewIcon />}
            size="xs"
            variant="outline"
            colorScheme="gray"
            bg="white"
            color={THEME_COLORS.text.primary}
            borderColor={THEME_COLORS.border}
            _hover={{
              bg: 'gray.50',
              color: `${UI_CONFIG.TEAL_THEME}.600`,
              borderColor: `${UI_CONFIG.TEAL_THEME}.300`,
              transform: 'scale(1.1)',
            }}
            _active={{
              bg: 'gray.100',
              transform: 'scale(0.9)',
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleViewGroupMembers(item);
            }}
            ml={1}
            mr={1}
            minW="auto"
            h="18px"
            w="18px"
            borderRadius="full"
            transition="all 0.2s"
          />
        </Tooltip>
      )}

      {/* Remove Button */}
      <Tooltip label="Remove" fontSize="xs" placement="top" hasArrow>
        <Box
          as="button"
          ml={1}
          w="16px"
          h="16px"
          borderRadius="full"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="rgba(255,255,255,0.3)"
          color="white"
          fontSize="xs"
          _hover={{
            bg: 'rgba(255,255,255,0.5)',
            transform: 'scale(1.1)',
          }}
          onClick={() => onItemRemove(item)}
          transition="all 0.2s"
        >
          ✕
        </Box>
      </Tooltip>
    </Tag>
  );

  /**
   * Renders the group members modal
   */
  const renderGroupMembersModal = () => (
    <Modal isOpen={isOpen} onClose={onClose} size={UI_CONFIG.MODAL_SIZE}>
      <ModalOverlay />
      <ModalContent>
        {/* Modal Header */}
        <ModalHeader pb={2}>
          <VStack align="start" spacing={2}>
            <HStack>
              <Text fontSize="lg" fontWeight="bold">
                Group Members
              </Text>
              {selectedGroup && (
                <Badge
                  colorScheme={UI_CONFIG.TEAL_THEME}
                  variant="subtle"
                  fontSize="sm"
                >
                  {selectedGroup.label}
                </Badge>
              )}
            </HStack>
            {!loadingMembers && groupMembers.length > 0 && (
              <Text fontSize="sm" color={THEME_COLORS.text.primary}>
                {groupMembers.length} member
                {groupMembers.length !== 1 ? 's' : ''} found
              </Text>
            )}
          </VStack>
        </ModalHeader>

        <Divider />
        <ModalCloseButton />

        {/* Modal Body */}
        <ModalBody py={4}>
          {loadingMembers ? (
            // Loading State
            <Flex justify="center" align="center" py={12}>
              <VStack spacing={4}>
                <Spinner
                  size={UI_CONFIG.SPINNER_SIZE}
                  color={`${UI_CONFIG.TEAL_THEME}.500`}
                  thickness="4px"
                />
                <Text color={THEME_COLORS.text.primary}>
                  Loading group members...
                </Text>
              </VStack>
            </Flex>
          ) : (
            // Members List
            <Box maxH={UI_CONFIG.MAX_MODAL_HEIGHT} overflowY="auto">
              {groupMembers.length > 0 ? (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {groupMembers.map((member, index) => (
                    <Box
                      key={member.id || index}
                      p={4}
                      border="1px"
                      borderColor={THEME_COLORS.border}
                      borderRadius="lg"
                      bg="white"
                      shadow="sm"
                      _hover={{
                        shadow: 'md',
                        borderColor: `${UI_CONFIG.TEAL_THEME}.300`,
                      }}
                      transition="all 0.2s"
                    >
                      <HStack spacing={3} align="start">
                        <Avatar
                          size="sm"
                          name={sanitizeDisplayName(
                            member.displayName || member.name
                          )}
                          bg={`${UI_CONFIG.TEAL_THEME}.500`}
                          color="white"
                        />
                        <VStack align="start" spacing={1} flex={1}>
                          <Text
                            fontWeight="semibold"
                            fontSize="sm"
                            lineHeight="1.2"
                          >
                            {member.displayName ||
                              member.name ||
                              'Unknown User'}
                          </Text>
                          {member.mail && (
                            <Text
                              fontSize="xs"
                              color="blue.600"
                              lineHeight="1.2"
                            >
                              📧 {member.mail}
                            </Text>
                          )}
                          {member.userPrincipalName &&
                            member.userPrincipalName !== member.mail && (
                              <Text
                                fontSize="xs"
                                color={THEME_COLORS.text.secondary}
                                lineHeight="1.2"
                              >
                                👤 {member.userPrincipalName}
                              </Text>
                            )}
                          {member.jobTitle && (
                            <Text
                              fontSize="xs"
                              color="purple.600"
                              lineHeight="1.2"
                            >
                              💼 {member.jobTitle}
                            </Text>
                          )}
                        </VStack>
                      </HStack>
                    </Box>
                  ))}
                </SimpleGrid>
              ) : (
                // Empty State
                <VStack spacing={4} py={8}>
                  <Text fontSize="4xl">👥</Text>
                  <VStack spacing={2}>
                    <Text
                      textAlign="center"
                      fontWeight="semibold"
                      color={THEME_COLORS.text.primary}
                    >
                      No members found
                    </Text>
                    <Text
                      textAlign="center"
                      fontSize="sm"
                      color={THEME_COLORS.text.secondary}
                    >
                      This group appears to be empty or you don&apos;t have
                      permission to view its members.
                    </Text>
                  </VStack>
                </VStack>
              )}
            </Box>
          )}
        </ModalBody>

        <Divider />

        {/* Modal Footer */}
        <ModalFooter>
          <HStack spacing={3}>
            <Text fontSize="sm" color={THEME_COLORS.text.secondary}>
              {selectedGroup?.label}
            </Text>
            <Button colorScheme={UI_CONFIG.TEAL_THEME} onClick={onClose}>
              Close
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  // ==============================================================================================
  // MAIN RENDER
  // ==============================================================================================

  return (
    <FormControl isInvalid={isError}>
      <FormLabel>{label}</FormLabel>

      {/* Search Type Toggle */}
      {renderSearchTypeToggle()}

      {/* Group Limitation Alert */}
      {renderGroupAlert()}

      {/* Search Input */}
      {renderSearchInput()}

      {/* Selected Items Display */}
      <Box>{selectedItems?.map(renderSelectedItem)}</Box>

      {/* Error Message */}
      {isError && <FormErrorMessage>{errorMessage}</FormErrorMessage>}

      {/* Helper Text */}
      <FormHelperText>{helperText}</FormHelperText>

      {/* Group Members Modal */}
      {renderGroupMembersModal()}
    </FormControl>
  );
}

// ================================================================================================
// COMPONENT CONFIGURATION
// ================================================================================================

/** PropTypes definition for type checking */
MultiEmailInput.propTypes = {
  label: PropTypes.string.isRequired,
  helperText: PropTypes.string.isRequired,
  selectedItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      type: PropTypes.oneOf([SEARCH_TYPES.USERS, SEARCH_TYPES.GROUPS])
        .isRequired,
      data: PropTypes.object,
    })
  ).isRequired,
  isError: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string.isRequired,
  onItemAdd: PropTypes.func.isRequired,
  onItemRemove: PropTypes.func.isRequired,
  defaultSearchType: PropTypes.oneOf([SEARCH_TYPES.USERS, SEARCH_TYPES.GROUPS]),
};

/** Default props */
MultiEmailInput.defaultProps = {
  defaultSearchType: SEARCH_TYPES.USERS,
};

export default MultiEmailInput;
